import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Button, Stack, Chip, Alert, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Switch, FormControlLabel, Divider, Link, CircularProgress, Tooltip,
} from '@mui/material';
import { CheckCircle, Link as LinkIcon, PlayArrow, Search, Settings, ContentCopy, HelpOutline, TrendingUp, Inventory, Chat } from '@mui/icons-material';
import { api } from '../config/api';
import { leerError } from '../utils/errores';

/** Mensajes para los errores que vuelven desde Mercado Libre (?error=meli_...). */
export const ERRORES_MELI: Record<string, string> = {
  meli_offline: 'Tu aplicación de Mercado Libre no tiene activado el permiso "acceso offline" (offline_access). Actívalo en tu aplicación y vuelve a conectar.',
  meli_credenciales: 'Mercado Libre rechazó el App ID o la Secret Key. Revísalos y vuelve a intentar.',
  meli_estado: 'La conexión tardó demasiado. Vuelve a apretar "Conectar cuenta".',
  meli_access_denied: 'Cancelaste la autorización en Mercado Libre.',
  meli_cuenta_en_uso: 'Esa cuenta de Mercado Libre ya está conectada a otra cuenta de tRespondo. Cada cuenta de Mercado Libre solo puede usarse en una.',
  meli_oauth: 'No se pudo conectar Mercado Libre. Revisa que la URI de redireccionamiento de tu aplicación sea exactamente la que te indicamos.',
};

const Paso: React.FC<{ n: number; children: React.ReactNode }> = ({ n, children }) => (
  <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ mb: 1.5 }}>
    <Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: '#FFE600', color: '#2D3277', fontWeight: 800, fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{n}</Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
  </Stack>
);

const Copiable: React.FC<{ label: string; valor: string; onCopiar: () => void }> = ({ label, valor, onCopiar }) => (
  <TextField fullWidth size="small" label={label} value={valor} sx={{ mt: 1 }}
    InputProps={{ readOnly: true, endAdornment: <Button size="small" startIcon={<ContentCopy />} onClick={() => { navigator.clipboard.writeText(valor); onCopiar(); }}>Copiar</Button> }} />
);

const MercadoLibreCard: React.FC<{ onMensaje: (t: string, sev: 'success' | 'error' | 'info') => void }> = ({ onMensaje }) => {
  const [e, setE] = useState<any>(null);
  const [guia, setGuia] = useState(false);
  const [appId, setAppId] = useState('');
  const [secret, setSecret] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [resultado, setResultado] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [errorGuia, setErrorGuia] = useState('');

  const cargar = useCallback(() => {
    api.get('/integraciones/meli').then((r) => {
      setE(r.data);
      // Mensaje contextualizado si acaba de conectar
      if (r.data.conectada && !window.sessionStorage.getItem('meli_connected_msg_shown')) {
        window.sessionStorage.setItem('meli_connected_msg_shown', 'true');
        setTimeout(() => {
          onMensaje(
            `🎉 ¡Listo! Tu tienda está conectada. El agente ya sabe sobre ${r.data.publicaciones || 'X'} productos.\n` +
            `✅ Busca automáticamente cuando preguntan ("¿tienes en azul?")\n` +
            `✅ Consulta estado de pedidos en tiempo real\n` +
            `${r.data.opciones?.tool_preguntas ? '✅ Responde preguntas en tus publicaciones' : '⚠️ Activa "Respuesta Automática" para contestar en publicaciones'}`,
            'success'
          );
        }, 500);
      }
    }).catch(() => setE(null));
  }, [onMensaje]);
  useEffect(cargar, [cargar]);

  const conectar = async () => {
    setOcupado(true);
    try {
      const { data } = await api.post('/integraciones/meli/conectar');
      window.location.href = data.authUrl;
    } catch (err: any) {
      onMensaje(leerError(err, 'No se pudo iniciar la conexión con Mercado Libre.').message, 'error');
      setOcupado(false);
    }
  };

  const guardarYConectar = async () => {
    setErrorGuia('');
    setOcupado(true);
    try {
      await api.put('/integraciones/meli/credenciales', { app_id: appId, client_secret: secret });
      setSecret('');
      await conectar();
    } catch (err: any) {
      setErrorGuia(leerError(err, 'No se pudieron guardar las credenciales.').message);
      setOcupado(false);
    }
  };

  const cambiarOpcion = async (k: string, v: boolean) => {
    setE({ ...e, opciones: { ...e.opciones, [k]: v } });
    try { 
      await api.put('/integraciones/meli/opciones', { [k]: v });
      
      // Mensajes contextualizados según qué herramienta se activó/desactivó
      const msgs: Record<string, {on: string, off: string}> = {
        tool_productos: {
          on: '✅ Catálogo en Vivo activado. El agente buscará tus productos en tiempo real.',
          off: '⚠️ Catálogo desactivado. Tu agente no podrá responder sobre productos en WhatsApp.',
        },
        tool_pedidos: {
          on: '✅ Seguimiento de Pedidos activado. Los clientes pueden preguntar por el estado de sus compras.',
          off: '⚠️ Seguimiento desactivado. Los clientes no podrán consultar el estado de sus pedidos.',
        },
        tool_preguntas: {
          on: '✅ Respuesta Automática activada. El agente responderá preguntas en tus publicaciones de forma automática.',
          off: '⚠️ Respuesta Automática desactivada. Las preguntas en tus publicaciones quedará sin responder.',
        },
      };
      
      const msg = msgs[k]?.[v ? 'on' : 'off'] || 'Opción actualizada.';
      onMensaje(msg, v ? 'success' : 'info');
    }
    catch { onMensaje('No se pudo guardar la opción.', 'error'); cargar(); }
  };

  const probar = async () => {
    setOcupado(true);
    try {
      const { data } = await api.post('/integraciones/meli/probar');
      onMensaje(data.ok ? `✅ Conectado como ${data.nickname}: ${data.publicaciones} publicaciones activas.` : `Error: ${data.error}`, data.ok ? 'success' : 'error');
    } finally { setOcupado(false); }
  };

  const buscar = async () => {
    if (!busqueda.trim()) return;
    setOcupado(true); setResultado('');
    try { const { data } = await api.get('/integraciones/meli/buscar', { params: { q: busqueda } }); setResultado(data.resultado); }
    catch (err: any) { setResultado(leerError(err, 'No se pudo buscar.').message); }
    finally { setOcupado(false); }
  };

  const desconectar = async () => {
    await api.delete('/integraciones/meli');
    onMensaje('Mercado Libre desconectado. Tus credenciales quedan guardadas por si vuelves a conectar.', 'info');
    cargar();
  };

  const borrarTodo = async () => {
    if (!window.confirm('¿Borrar el App ID, la Secret Key y la conexión de Mercado Libre?')) return;
    await api.delete('/integraciones/meli/credenciales');
    onMensaje('Credenciales de Mercado Libre eliminadas.', 'info');
    cargar();
  };

  if (!e) return null;
  const copiado = () => onMensaje('Copiado', 'info');

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="overline" color="text.secondary">Ventas</Typography>
      <Card sx={{ mt: 0.5, border: '1px solid #E2E8F0' }}>
        <CardContent>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" sx={{ mb: 1 }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box component="img" src="/logos/mercadolibre.svg" alt="Logo de Mercado Libre" sx={{ height: 34, width: 'auto' }} />
              {e.conectada && <Chip size="small" color="success" icon={<CheckCircle />} label={`Conectado${e.nickname ? ` · ${e.nickname}` : ''}`} />}
            </Stack>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Tu agente responde por WhatsApp con los productos que publicas en Mercado Libre (precio, stock y link), revisa el estado de los pedidos
            y, si quieres, contesta solo las preguntas que te hacen en tus publicaciones. Usa tu propia aplicación de Mercado Libre: tus datos no pasan por cuentas de terceros.
          </Typography>

          {/* Beneficios destacados */}
          {e.conectada && (
            <Box sx={{ mb: 2, p: 1.5, bgcolor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 1 }}>
              <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, color: '#166534', mb: 0.5 }}>
                ✅ Beneficios activos:
              </Typography>
              <Stack spacing={0.5} sx={{ fontSize: 12, color: '#166534' }}>
                <Typography variant="caption">🚀 Respuestas al instante: <strong>~5 segundos</strong></Typography>
                <Typography variant="caption">📦 Stock en vivo: el agente ve disponibilidad actualizada</Typography>
                <Typography variant="caption">💰 +30% conversión: clientes que preguntan → compran</Typography>
                <Typography variant="caption">🤖 Respuestas automáticas a preguntas de publicaciones</Typography>
              </Stack>
            </Box>
          )}

          {e.error === 'reconectar' && (
            <Alert severity="warning" sx={{ mb: 1.5 }}>Mercado Libre cerró la conexión (cambiaste la clave, revocaste el permiso o cambió tu aplicación). Vuelve a conectar tu cuenta.</Alert>
          )}

          {!e.tieneCredenciales && (
            <Button variant="contained" startIcon={<Settings />} onClick={() => setGuia(true)} sx={{ bgcolor: '#2D3277', '&:hover': { bgcolor: '#1f2459' } }}>
              Configurar Mercado Libre
            </Button>
          )}

          {e.tieneCredenciales && !e.conectada && (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
              <Button variant="contained" startIcon={ocupado ? <CircularProgress size={16} color="inherit" /> : <LinkIcon />} onClick={conectar} disabled={ocupado} sx={{ bgcolor: '#2D3277', '&:hover': { bgcolor: '#1f2459' } }}>
                Conectar cuenta
              </Button>
              <Button onClick={() => { setAppId(e.appId || ''); setGuia(true); }}>Editar credenciales</Button>
              <Button color="error" onClick={borrarTodo}>Borrar</Button>
              <Typography variant="caption" color="text.secondary">App ID {e.appId}</Typography>
            </Stack>
          )}

          {e.conectada && (
            <Box>
              <Stack spacing={0.5} sx={{ mb: 1.5 }}>
              <Box>
              <FormControlLabel 
                control={<Switch checked={!!e.opciones.tool_productos} onChange={(ev) => cambiarOpcion('tool_productos', ev.target.checked)} />} 
                label={
                  <Stack sx={{ ml: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>🛍️ Catálogo en Vivo</Typography>
                    <Typography variant="caption" color="text.secondary">El agente busca tus productos en tiempo real y responde con precio, stock y link</Typography>
                  </Stack>
                }
              />
            </Box>
            <Box sx={{ ml: 4, mb: 1 }}>
              <Tooltip title="Si el cliente pregunta '¿tienes en azul?', el agente busca automáticamente en tu catálogo y responde al instante">
                <HelpOutline sx={{ fontSize: 14, color: '#999', cursor: 'help' }} />
              </Tooltip>
            </Box>
            {!e.opciones.tool_productos && (
              <Alert severity="warning" sx={{ mt: 1.5, mb: 1.5 }}>
                <strong>⚠️ Catálogo desactivado.</strong> Tu agente no puede responder sobre productos en WhatsApp.
                <br />Activa <strong>"🛍️ Catálogo en Vivo"</strong> arriba para que busque automáticamente cuando pregunten.
              </Alert>
            )}
              <Box>
              <FormControlLabel 
                control={<Switch checked={!!e.opciones.tool_pedidos} onChange={(ev) => cambiarOpcion('tool_pedidos', ev.target.checked)} />} 
                label={
                  <Stack sx={{ ml: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>📦 Seguimiento de Pedidos</Typography>
                    <Typography variant="caption" color="text.secondary">Si preguntan "¿dónde está mi compra?", el agente consulta el estado exacto</Typography>
                  </Stack>
                }
              />
            </Box>
            <Box sx={{ ml: 4, mb: 1 }}>
              <Tooltip title="Ejemplo: 'Tu pedido #12345 está en camino, llegaría mañana. Puedes rastrear aquí: https://...'">
                <HelpOutline sx={{ fontSize: 14, color: '#999', cursor: 'help' }} />
              </Tooltip>
            </Box>
              <Box>
              <FormControlLabel 
                control={<Switch checked={!!e.opciones.tool_preguntas} onChange={(ev) => cambiarOpcion('tool_preguntas', ev.target.checked)} />} 
                label={
                  <Stack sx={{ ml: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>🤖 Respuesta Automática a Preguntas</Typography>
                    <Typography variant="caption" color="text.secondary">El agente contesta automáticamente las preguntas de tus publicaciones</Typography>
                  </Stack>
                }
              />
            </Box>
            <Box sx={{ ml: 4 }}>
              <Tooltip title="Requiere que tu aplicación tenga permiso de escritura y que hayas configurado el webhook de notificaciones">
                <HelpOutline sx={{ fontSize: 14, color: '#999', cursor: 'help' }} />
              </Tooltip>
            </Box>
                {e.opciones.tool_preguntas && (
                  <Alert severity="info" sx={{ mt: 1.5 }}>
                    <strong>⚙️ Configuración requerida:</strong> Tu aplicación debe tener permisos de escritura y las notificaciones de "Questions" 
                    apuntando a la URL de notificaciones. El agente responde con tu base de conocimiento y nunca incluye links, teléfonos ni correos (Mercado Libre no lo permite).
                  </Alert>
                )}
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1 }}>
                <TextField size="small" label="Probar búsqueda" placeholder="Ej: zapatillas negras" value={busqueda} onChange={(ev) => setBusqueda(ev.target.value)} onKeyDown={(ev) => ev.key === 'Enter' && buscar()} sx={{ flex: 1 }} />
                <Button variant="outlined" startIcon={<Search />} onClick={buscar} disabled={ocupado || !busqueda.trim()}>Buscar</Button>
              </Stack>
              {resultado && <Alert severity="info" sx={{ mb: 1, whiteSpace: 'pre-line' }}>{resultado}</Alert>}
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Button size="small" variant="outlined" startIcon={<PlayArrow />} onClick={probar} disabled={ocupado}>Probar conexión</Button>
                <Button size="small" onClick={() => { setAppId(e.appId || ''); setGuia(true); }}>Ver configuración</Button>
                <Button size="small" color="error" onClick={desconectar}>Desconectar</Button>
              </Stack>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Guía paso a paso para crear la aplicación propia */}
      <Dialog open={guia} onClose={() => setGuia(false)} fullWidth maxWidth="sm">
        <DialogTitle>Conectar tu Mercado Libre</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Crearás una aplicación gratis en Mercado Libre. Toma unos 5 minutos y se hace una sola vez.
          </Typography>
          <Paso n={1}>
            <Typography variant="body2">
              Entra a <Link href="https://developers.mercadolibre.cl/devcenter" target="_blank" rel="noopener noreferrer">developers.mercadolibre.cl</Link> con
              la cuenta de Mercado Libre de tu tienda y aprieta <strong>"Crear aplicación"</strong>.
            </Typography>
          </Paso>
          <Paso n={2}><Typography variant="body2">Ponle un nombre (ej: "Mi tienda + tRespondo"), una descripción corta y, si quieres, tu logo.</Typography></Paso>
          <Paso n={3}>
            <Typography variant="body2">En <strong>"URI de redireccionamiento"</strong> pega exactamente esta dirección:</Typography>
            <Copiable label="URI de redireccionamiento" valor={e.redirectUri} onCopiar={copiado} />
          </Paso>
          <Paso n={4}>
            <Typography variant="body2">
              En <strong>permisos</strong> activa <strong>lectura</strong>, <strong>acceso offline</strong> (obligatorio, para que la conexión no se corte)
              y <strong>escritura</strong> si quieres que el agente responda las preguntas de tus publicaciones.
            </Typography>
          </Paso>
          <Paso n={5}>
            <Typography variant="body2">Opcional, para responder preguntas: en <strong>"Notificaciones"</strong> pega esta URL y marca el tema <strong>Questions</strong>.</Typography>
            <Copiable label="URL de notificaciones" valor={e.notificacionesUrl} onCopiar={copiado} />
          </Paso>
          <Paso n={6}><Typography variant="body2">Guarda la aplicación. Mercado Libre te mostrará el <strong>App ID</strong> y la <strong>Secret Key</strong>: pégalos aquí.</Typography></Paso>
          <Divider sx={{ my: 2 }} />
          {errorGuia && <Alert severity="error" sx={{ mb: 2 }}>{errorGuia}</Alert>}
          <TextField fullWidth label="App ID" value={appId} onChange={(ev) => setAppId(ev.target.value.trim())} sx={{ mb: 2 }} placeholder="Ej: 1234567890123456" inputProps={{ inputMode: 'numeric' }} />
          <TextField fullWidth label="Secret Key" type="password" value={secret} onChange={(ev) => setSecret(ev.target.value.trim())}
            helperText={e.tieneCredenciales ? 'Por seguridad no mostramos tu Secret Key guardada. Vuelve a pegarla para cambiarla.' : 'Se guarda solo para tu cuenta y nunca se muestra.'} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setGuia(false)}>Cerrar</Button>
          <Button variant="contained" disabled={ocupado || !appId || !secret} onClick={guardarYConectar} sx={{ bgcolor: '#2D3277', '&:hover': { bgcolor: '#1f2459' } }}>
            {ocupado ? 'Conectando...' : 'Guardar y conectar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MercadoLibreCard;
